import type { IErrorApiResponse, ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseQueryOptions,
  restrictApi,
  useQuery,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import {
  IWcwBrand,
  IWcwModel,
  IWcwSet,
  IWcwSupplierAvailability,
  IWcwVersion,
} from '~/purchase/types';

export type {
  IWcwBrand,
  IWcwModel,
  IWcwSet,
  IWcwSupplierAvailability,
  IWcwVersion,
};

type Options = {
  getBrands?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<Array<IWcwBrand>>, IErrorApiResponse>
  > & { args: { customerId: string } };
  getModels?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<Array<IWcwModel>>, IErrorApiResponse>
  > & { args: { customerId: string; brand: string } };
  getVersions?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<Array<IWcwVersion>>, IErrorApiResponse>
  > & { args: { customerId: string; brand: string; model: string } };
  getSupplierAvailability?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IWcwSupplierAvailability>>,
      IErrorApiResponse
    >
  > & { args: { customerId: string } };
};

export const useWcwCatalog = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const getBrandsQuery = useQuery({
    queryKey: PurchaseQueryKeys.wcw.brands(
      options.getBrands?.args.customerId ?? '',
    ),
    queryFn: async (): Promise<ISuccessApiResponse<Array<IWcwBrand>>> => {
      const customerId = options.getBrands!.args.customerId;
      const res = await callCoreApi<
        ISuccessApiResponse<Array<{ brand: string }>>
      >(
        `/tenants/api/wtb/tenants/${tenantCode}/customers/${customerId}/wheels/vehicles/brands`,
      );
      return {
        ...res,
        data: res.data.map(({ brand }) => ({ publicKey: brand, name: brand })),
      };
    },
    refetchOnWindowFocus: false,
    gcTime: Infinity,
    staleTime: Infinity,
    enabled: false,
    ...options.getBrands,
  });

  const getModelsQuery = useQuery({
    queryKey: PurchaseQueryKeys.wcw.models(
      options.getModels?.args.customerId ?? '',
      options.getModels?.args.brand ?? '',
    ),
    queryFn: async (): Promise<ISuccessApiResponse<Array<IWcwModel>>> => {
      const { customerId, brand } = options.getModels!.args;
      const res = await callCoreApi<
        ISuccessApiResponse<Array<{ model: string }>>
      >(
        `/tenants/api/wtb/tenants/${tenantCode}/customers/${customerId}/wheels/vehicles/models?brand=${encodeURIComponent(brand)}`,
      );
      return {
        ...res,
        data: res.data.map(({ model }) => ({ publicKey: model, name: model })),
      };
    },
    refetchOnWindowFocus: false,
    gcTime: Infinity,
    staleTime: Infinity,
    enabled: false,
    ...options.getModels,
  });

  const getVersionsQuery = useQuery({
    queryKey: PurchaseQueryKeys.wcw.versions(
      options.getVersions?.args.customerId ?? '',
      options.getVersions?.args.brand ?? '',
      options.getVersions?.args.model ?? '',
    ),
    queryFn: async (): Promise<ISuccessApiResponse<Array<IWcwVersion>>> => {
      const { customerId, brand, model } = options.getVersions!.args;
      const res = await callCoreApi<
        ISuccessApiResponse<Array<{ version: string }>>
      >(
        `/tenants/api/wtb/tenants/${tenantCode}/customers/${customerId}/wheels/vehicles/versions?brand=${encodeURIComponent(brand)}&model=${encodeURIComponent(model)}`,
      );
      return {
        ...res,
        data: res.data.map(({ version }) => ({
          publicKey: version,
          name: version,
        })),
      };
    },
    refetchOnWindowFocus: false,
    gcTime: Infinity,
    staleTime: Infinity,
    enabled: false,
    ...options.getVersions,
  });

  const getSupplierAvailabilityQuery = useQuery({
    queryKey: PurchaseQueryKeys.wcw.supplierAvailability(
      options.getSupplierAvailability?.args.customerId ?? '',
    ),
    queryFn: async (): Promise<
      ISuccessApiResponse<Array<IWcwSupplierAvailability>>
    > => {
      const customerId = options.getSupplierAvailability!.args.customerId;
      return callCoreApi<ISuccessApiResponse<Array<IWcwSupplierAvailability>>>(
        `/tenants/api/wtb/tenants/${tenantCode}/customers/${customerId}/wheels/supplier-availability`,
      );
    },
    refetchOnWindowFocus: false,
    gcTime: Infinity,
    staleTime: Infinity,
    enabled: false,
    ...options.getSupplierAvailability,
  });

  return restrictApi(options, {
    getBrands: getBrandsQuery,
    getModels: getModelsQuery,
    getVersions: getVersionsQuery,
    getSupplierAvailability: getSupplierAvailabilityQuery,
  });
};
